import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, RefreshCw, Save, Trash2, UploadCloud } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { publishAgent } from "../../services/agentPublishService";
import type {
  AgentPublishFeatureType,
  AgentPublishType,
  AgentPublishVersion,
} from "../../services/agentPublishService";

import LeadCallLogsTab from "../assistants/LeadCallLogsTab";
import PublishAgentDialog from "../assistants/PublishAgentDialog";
import TestAgentDialog from "./ImprovedTestAgentDialog";
import AgentTab from "./tabs/AgentTab";
import ConfigurationTab from "./tabs/ConfigurationTab";
import PromptTab from "./tabs/PromptTab";
import SubAgentsTab from "./tabs/SubAgentsTab";
import KnowledgeBaseTab from "./tabs/KnowledgeBaseTab";

const assistantFormSchema = z.object({
  assistantName: z.string().min(1, { message: "Assistant name is required" }),
  useIntegratedAgent: z.boolean().default(false),
  llm_provider: z.string().min(1, { message: "Model provider is required" }),
  llm_model: z.string().min(1, { message: "Model version is required" }),
  baseUrl: z.string().optional().or(z.literal("")),
  apiKey: z.string().optional().or(z.literal("")),
  // Realtime (speech-to-speech) providers, e.g. Gemini Live
  realtimeVoice: z.string().optional().or(z.literal("")),
  realtimeLanguage: z.string().optional().or(z.literal("")),
  // OpenAI-only request tier. Defaults to "priority", which is what the backend
  // hardcoded before this was configurable -- keeps existing assistants unchanged.
  serviceTier: z.enum(["auto", "default", "priority"]).default("priority"),
  temperature: z.number().min(0).max(1).default(0.7),
  maxTokens: z.number().int().positive().default(1024),
  detectEmotion: z.boolean().default(false),
  firstMessage: z.string().default("Hello, how can I help you today?"),
  endCallMessage: z.string().default("Goodbye, have a great day!"),

  // V2 phone calls only (src/telephonyProviders.js). Empty = no per-agent
  // preference, the V2 server's own default carrier applies.
  telephony_provider: z.string().optional().or(z.literal("")),

  stt_provider: z.string().min(1, { message: "Provider is required" }),
  stt_model: z.string().min(1, { message: "Model is required" }),
  language: z
    .string()
    .min(1, { message: "Language is required" })
    .default("en"),
  enableFiltering: z.boolean().default(false),
  useNumerals: z.boolean().default(false),
  backgroundDenoising: z.boolean().default(false),
  keyterms: z.string().optional(),
  endpointing_ms: z.number().int().min(100).max(5000).default(1000),
  enable_language_detection: z.boolean().default(false),
  // Kept separate from the TTS `sampleRate` field: stt_configuration and
  // tts_configuration are flattened into one form object, so a shared key would
  // let Murf TTS and Gnani/Smallest STT overwrite each other.
  stt_sample_rate: z.number().int().optional(),
  // Sarvam realtime's output mode (codemix/translit/etc). Only read when
  // stt_provider === "sarvam-realtime" — see AgentService.buildCallConfig in
  // the v2 server for the backend side of this crossover.
  stt_mode: z.string().optional(),

  tts_provider: z
    .enum([
      "elevenlabs", "smallestai", "custom", "groq", "cartesia", "deepgram", "sarvam", "resemble",
      "aws", "azure", "baseten", "google", "hume", "inworld", "lmnt", "mistralai", "murf",
      "neuphonic", "openai", "playai", "rime", "speechify", "spitch", "voxi-tts"
    ])
    .default("elevenlabs"),
  voice: z
    .string()
    .min(1, { message: "Voice is required" })
    .default("21m00Tcm4TlvDq8ikWAM"),
  tts_model: z
    .string()
    .min(1, { message: "TTS model is required" })
    .default("eleven_turbo_v2"),
  // Cartesia's TTS language, kept separate from the STT `language` field above
  // (see its comment) — they used to share one form key, which meant picking
  // a Cartesia TTS language silently overwrote the transcriber's language and
  // the TTS choice itself was never actually persisted to tts_configuration.
  tts_language: z.string().optional(),
  speed: z.union([z.number().min(0.5).max(2.0), z.string()]).default(1.0),
  pitch: z.number().min(-1.0).max(2).default(1.0),
  loudness: z.number().min(0.5).max(2.0).default(1.0),
  stability: z.number().min(0).max(1).default(0.5),
  similarity_boost: z.number().min(0).max(1).default(0.75),
  style: z.number().min(0).max(1).default(0),
  clarity: z.number().min(0).max(1).default(0.75),
  styleExaggeration: z.number().min(0).max(1).default(0),
  backgroundSound: z.string().default("Default"),
  backgroundSoundUrl: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine(
      (value) => {
        if (value && value.length > 0) {
          try {
            new URL(value);
            return true;
          } catch {
            return false;
          }
        }
        return true;
      },
      { message: "Please enter a valid URL" }
    ),
  inputMinCharacters: z.number().int().nonnegative().default(10),
  punctuationBoundaries: z.string().default("No Punctuation Boundaries Added"),

  optimizeStreaming: z.number().min(0).max(4).default(0),
  useSpeakerBoost: z.boolean().default(false),

  turnDetectionLanguage: z.enum(["english", "multilingual"]).default("english"),

  functions: z
    .array(
      z.object({
        name: z.string().min(1),
        description: z.string(),
        parameters: z.array(
          z.object({
            name: z.string().min(1),
            type: z.string().min(1),
            description: z.string(),
            required: z.boolean().default(false),
          })
        ),
      })
    )
    .default([]),

  systemPrompt: z.string().default(""),
  contextWindow: z.number().int().positive().default(4096),
  responseFormat: z.string().default("JSON"),
  enableStreaming: z.boolean().default(true),
  waitSeconds: z.number().min(0).max(2).optional(),
  smartEndpointing: z.boolean().default(false),
  onPunctuationSeconds: z.number().min(0).max(3).optional(),
  onNoPunctuationSeconds: z.number().min(0).max(3).optional(),
  onNumberSeconds: z.number().min(0).max(3).optional(),
  voicemailDetectionProvider: z.string().optional(),
  numberOfWords: z.number().min(0).max(10).optional(),
  voiceSeconds: z.number().min(0).max(0.5).optional(),
  backOffSeconds: z.number().min(0).max(10).optional(),
  minEndpointingDelay: z.number().min(0).max(10).default(0.5),
  maxEndpointingDelay: z.number().min(0.1).max(15).default(5.0),
  serverUrl: z.string().optional(),
  voicemailMessage: z.string().optional(),
  idleMessages: z.string().optional(),
  maxIdleMessages: z.number().min(0).max(10).optional(),
  idleTimeout: z.number().min(0).max(60).optional(),

  summaryPrompt: z.string().optional(),
  summaryTimeout: z.number().min(1).max(30).optional(),
  evaluationPrompt: z.string().optional(),
  evaluationRubric: z.string().optional(),
  evaluationTimeout: z.number().min(1).max(30).optional(),
  structuredDataPrompt: z.string().optional(),
  structuredJSONSchema: z.string().optional(),

  idleTime: z.number().int().nonnegative().default(10),
  idleRepeatCount: z.number().int().nonnegative().default(3),
  idleMessage: z.string().default("Are you still there?"),
  idleHangUpMessage: z
    .string()
    .default("It seems you're no longer available. Goodbye."),

  // VAD Configuration
  vadMinSpeechDuration: z.number().min(0.05).max(1.0).default(0.25),
  vadMinSilenceDuration: z.number().min(0.1).max(2.0).default(0.4),
  vadPrefixPaddingDuration: z.number().min(0.0).max(1.0).default(0.0),
  vadActivationThreshold: z.number().min(0.1).max(1.0).default(0.7),

  // Background Audio Configuration
  background_audio_enabled: z.boolean().default(true),
  ambient_sound_type: z.enum(["crowded_room", "office_ambience", "none"]).default("crowded_room"),
  ambient_sound_volume: z.number().min(0).max(1).default(0.1),
  thinking_sound_type: z.enum(["office_ambience", "crowded_room", "none"]).default("office_ambience"),
  thinking_sound_volume: z.number().min(0).max(1).default(1.0),

  // Turn Detection Configuration
  turnDetectionEnabled: z.boolean().default(true),
  turnDetectionThreshold: z.number().min(0.1).max(1.0).optional(),
  minInterruptionDuration: z.number().min(0.1).max(2.0).default(0.2),

  // Knowledge Base Configuration
  knowledgeBaseEnabled: z.boolean().default(false),
  knowledgeBaseSearchThreshold: z.number().min(0).max(1).default(0.7),
  knowledgeBaseMaxResults: z.number().int().min(1).max(10).default(3),
  knowledgeBasePromptTemplate: z.string().optional().default("Here is relevant information from the knowledge base:\n{results}"),

  // Background Voice Cancellation Configuration
  enableBVC: z.boolean().default(true),
  bvcMode: z.enum(['standard', 'telephony']).default('telephony'),

  // Filler Sentences Configuration
  fillerEnabled: z.boolean().default(true),
  fillerPhrases: z.array(z.string()).default([]),

  // Agent Tool Configuration
  enableWaitForUser: z.boolean().default(false),
  enableCallScreening: z.boolean().default(false),
  enableEndCall: z.boolean().default(true),
  enableAnsweringMachine: z.boolean().default(false),
  enableCheckCtcBudget: z.boolean().default(false),
  enableSearchKnowledge: z.boolean().default(false),
  enableLoanEligibility: z.boolean().default(false),
  enableStartTechnicalQuestions: z.boolean().default(false),
  enableContactForm: z.boolean().default(false),
});

type AssistantFormValues = z.infer<typeof assistantFormSchema>;

interface AssistantDashboardProps {
  assistantId?: string | null;
  onAssistantDeleted?: (id: string) => void;
  onCancelAssistantSelection?: (query?: string) => void;
  onAssistantUpdated?: (id: string, updates: { name?: string; status?: string }) => void;
}

export default function AssistantDashboard({
  assistantId: propAssistantId,
  onAssistantDeleted,
  onCancelAssistantSelection,
  onAssistantUpdated,
}: AssistantDashboardProps = {}) {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("agent");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assistantId, setAssistantId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState<AssistantFormValues | null>(null);
  const [tabsWithErrors, setTabsWithErrors] = useState<string[]>([]);
  const [assistantName, setAssistantName] = useState("");
  const [assistantType, setAssistantType] = useState<'single' | 'multi'>('single');
  const [isPro, setIsPro] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isPublishingPlatform, setIsPublishingPlatform] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  useEffect(() => {
    if (propAssistantId) {
      setAssistantId(propAssistantId);
      fetchAssistant(propAssistantId);
    } else {
      const searchParams = new URLSearchParams(location.search);
      const id = searchParams.get("id");
      if (id) {
        setAssistantId(id);
        fetchAssistant(id);
      }
    }
  }, [location, propAssistantId]);

  useEffect(() => {
    if (propAssistantId) {
      setActiveTab("agent");
    }
  }, [propAssistantId]);

  useEffect(() => {
    if (currentOrganization?.id) {
      const checkProStatus = async () => {
        try {
          const { data, error } = await supabase
            .from("organizations")
            .select("is_pro")
            .eq("id", currentOrganization.id)
            .single();

          if (error) {
            console.error("Error fetching organization PRO status:", error);
            return;
          }

          setIsPro(data?.is_pro || false);
        } catch (error) {
          console.error("Error checking PRO status:", error);
        }
      };

      checkProStatus();
    }
  }, [currentOrganization]);

  const methods = useForm<AssistantFormValues>({
    resolver: zodResolver(assistantFormSchema),
    defaultValues: {
      assistantName: "",
      useIntegratedAgent: false,
      llm_provider: "openai",
      llm_model: "gpt-4o",
      serviceTier: "priority",
      temperature: 0.7,
      maxTokens: 1024,
      detectEmotion: false,
      firstMessage: "Hello, how can I help you today?",
      endCallMessage: "Goodbye, have a great day!",

      telephony_provider: "",

      language: "en",
      enableFiltering: false,
      useNumerals: false,
      backgroundDenoising: false,
      keyterms: "",

      voice: "21m00Tcm4TlvDq8ikWAM",
      tts_model: "eleven_turbo_v2",
      tts_provider: "elevenlabs",
      tts_language: "en",
      speed: 1.0,
      pitch: 1.0,
      stability: 0.5,
      clarity: 0.75,
      styleExaggeration: 0,
      backgroundSound: "Default",
      backgroundSoundUrl: "",
      inputMinCharacters: 10,
      punctuationBoundaries: "No Punctuation Boundaries Added",

      optimizeStreaming: 0,
      useSpeakerBoost: false,

      functions: [],

      systemPrompt: "",
      contextWindow: 4096,
      responseFormat: "JSON",
      enableStreaming: true,
      waitSeconds: 0.6,
      smartEndpointing: false,
      onPunctuationSeconds: 0.1,
      onNoPunctuationSeconds: 1.5,
      onNumberSeconds: 0.5,
      voicemailDetectionProvider: "select",
      numberOfWords: 0,
      voiceSeconds: 0.2,
      backOffSeconds: 1,
      serverUrl: "",
      voicemailMessage: "Please call back when you're available.",
      idleMessages: "Select idle messages",
      maxIdleMessages: 3,
      idleTimeout: 7.5,

      summaryPrompt:
        "You are an expert note-taker. You will be given a transcript of a call. Summarize the call in 2-3 sentences, if applicable.",
      summaryTimeout: 30,
      evaluationPrompt:
        "You are an expert call evaluator. You will be given a transcript of a call and the system prompt of the AI participant. Determine if the call was successful based on the objectives inferred from the system prompt.",
      evaluationRubric: "default",
      evaluationTimeout: 30,
      structuredDataPrompt:
        "You will be given a transcript of a call and the system prompt of the AI participant. Extract...",
      structuredJSONSchema: "",

      // Background Audio defaults
      background_audio_enabled: true,
      ambient_sound_type: "crowded_room",
      ambient_sound_volume: 0.1,
      thinking_sound_type: "office_ambience",
      thinking_sound_volume: 1.0,

      // Knowledge Base defaults
      knowledgeBaseEnabled: false,
      knowledgeBaseSearchThreshold: 0.7,
      knowledgeBaseMaxResults: 3,
      knowledgeBasePromptTemplate: "Here is relevant information from the knowledge base:\n{results}",

      // Turn Detection defaults
      turnDetectionEnabled: true,
      turnDetectionThreshold: undefined,
      minInterruptionDuration: 0.2,

      // Background Voice Cancellation defaults
      enableBVC: true,
      bvcMode: 'telephony',

      // Filler Sentences defaults
      fillerEnabled: true,
      fillerPhrases: [],

      // Agent Tool defaults
      enableWaitForUser: false,
      enableCallScreening: false,
      enableEndCall: true,
      enableAnsweringMachine: false,
      enableCheckCtcBudget: false,
      enableSearchKnowledge: false,
      enableLoanEligibility: false,
      enableStartTechnicalQuestions: false,
      enableContactForm: false,
    },
    mode: "onChange",
  });

  const fetchAssistant = async (id: string) => {
    setIsLoading(true);
    try {
      const { data: assistant, error } = await supabase
        .from("assistants")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      if (assistant) {
        const {
          name: assistantName,
          assistant_type,
          llm_provider,
          llm_model,
          llm_configuration,
          description,
          configuration,
          telephony_provider,
          stt_provider,
          stt_model,
          stt_configuration,
          tts_provider,
          tts_model,
          tts_configuration,
          system_prompt,
          summary_prompt,
          evaluation_prompt,
          structured_json_schema,
          structured_data_prompt,
          idle_message,
          idle_hangup_message,
          use_integrated_agent,
          ...rest
        } = assistant;

        setAssistantName(assistantName || "");
        setAssistantType(assistant_type || 'single');

        const mergedConfig = {
          ...configuration,
          ...llm_configuration,
          ...stt_configuration,
          ...tts_configuration,
        };

        const firstMessage =
          assistant.first_message || mergedConfig?.firstMessage;
        const endCallMessage =
          assistant.end_call_message || mergedConfig?.endCallMessage;

        // Convert numeric speed to string for Cartesia provider
        let speedValue = mergedConfig?.speed;
        if (tts_provider === 'cartesia' && typeof speedValue === 'number') {
          if (speedValue <= 0.9) speedValue = 'slow';
          else if (speedValue >= 1.1) speedValue = 'fast';
          else speedValue = 'normal';
        }

        // Convert keyterms array to space-separated string for form display
        let keytermsValue = mergedConfig?.keyterms;
        if (Array.isArray(keytermsValue)) {
          keytermsValue = keytermsValue.join(' ');
        }

        const resetData = {
          assistantName: assistantName || "",
          useIntegratedAgent: use_integrated_agent ?? false,
          llm_provider: llm_provider || "openai",
          llm_model: llm_model || "gpt-4o",
          telephony_provider: telephony_provider || "",
          firstMessage: firstMessage || "Hello, how can I help you today?",
          endCallMessage: endCallMessage || "Goodbye, have a great day!",
          systemPrompt: system_prompt || llm_configuration?.systemPrompt || "",
          summaryPrompt: summary_prompt || configuration?.summaryPrompt || "",
          evaluationPrompt:
            evaluation_prompt || configuration?.evaluationPrompt || "",
          structuredJSONSchema:
            structured_json_schema || configuration?.structuredJSONSchema || "",
          structuredDataPrompt:
            structured_data_prompt || configuration?.structuredDataPrompt || "",
          idleMessage:
            idle_message ||
            configuration?.idleMessage ||
            "Are you still there?",
          idleHangUpMessage:
            idle_hangup_message ||
            configuration?.idleHangUpMessage ||
            "It seems you're no longer available. Goodbye.",
          
          // VAD Configuration
          vadMinSpeechDuration: configuration?.vad?.min_speech_duration || 0.25,
          vadMinSilenceDuration: configuration?.vad?.min_silence_duration || 0.4,
          vadPrefixPaddingDuration: configuration?.vad?.prefix_padding_duration || 0.0,
          vadActivationThreshold: configuration?.vad?.activation_threshold || 0.7,

          // Background Audio Configuration
          background_audio_enabled: assistant.background_audio_enabled ?? true,
          ambient_sound_type: assistant.ambient_sound_type || "crowded_room",
          ambient_sound_volume: assistant.ambient_sound_volume ?? 0.1,
          thinking_sound_type: assistant.thinking_sound_type || "office_ambience",
          thinking_sound_volume: assistant.thinking_sound_volume ?? 1.0,

          // Endpointing Delay Configuration
          minEndpointingDelay: configuration?.endpointing?.min_endpointing_delay || 0.5,
          maxEndpointingDelay: configuration?.endpointing?.max_endpointing_delay || 5.0,

          // Knowledge Base Configuration
          knowledgeBaseEnabled: configuration?.knowledge_base?.enabled || false,
          knowledgeBaseSearchThreshold: configuration?.knowledge_base?.search_threshold || 0.7,
          knowledgeBaseMaxResults: configuration?.knowledge_base?.max_results || 3,
          knowledgeBasePromptTemplate: configuration?.knowledge_base?.prompt_template || "Here is relevant information from the knowledge base:\n{results}",

          // Turn Detection Configuration
          turnDetectionEnabled: configuration?.turnDetectionEnabled !== undefined ? configuration.turnDetectionEnabled : true,
          turnDetectionThreshold: configuration?.turnDetectionThreshold,
          minInterruptionDuration: configuration?.minInterruptionDuration || 0.2,

          // Background Voice Cancellation Configuration
          enableBVC: configuration?.enableBVC !== undefined ? configuration.enableBVC : true,
          bvcMode: configuration?.bvcMode || 'telephony',

          // Filler Sentences Configuration
          fillerEnabled: configuration?.fillerEnabled !== undefined ? configuration.fillerEnabled : true,
          fillerPhrases: Array.isArray(configuration?.fillerPhrases) ? configuration.fillerPhrases : [],

          // Agent Tool Configuration
          enableWaitForUser: configuration?.enableWaitForUser !== undefined ? configuration.enableWaitForUser : false,
          enableCallScreening: configuration?.enableCallScreening !== undefined ? configuration.enableCallScreening : false,
          enableEndCall: configuration?.enableEndCall !== undefined ? configuration.enableEndCall : true,
          enableAnsweringMachine: configuration?.enableAnsweringMachine !== undefined ? configuration.enableAnsweringMachine : false,
          enableCheckCtcBudget: configuration?.enableCheckCtcBudget !== undefined ? configuration.enableCheckCtcBudget : false,
          enableSearchKnowledge: configuration?.enableSearchKnowledge !== undefined ? configuration.enableSearchKnowledge : false,
          enableLoanEligibility: configuration?.enableLoanEligibility !== undefined ? configuration.enableLoanEligibility : false,
          enableStartTechnicalQuestions: configuration?.enableStartTechnicalQuestions !== undefined ? configuration.enableStartTechnicalQuestions : false,
          enableContactForm: configuration?.enableContactForm !== undefined ? configuration.enableContactForm : false,

          ...mergedConfig,
          keyterms: keytermsValue !== undefined ? keytermsValue : "",
          speed: speedValue || mergedConfig?.speed,
          tts_provider: tts_provider || "elevenlabs",
          tts_model: tts_model || "eleven_turbo_v2",
          // `language` (STT transcriber) and `tts_language` (Cartesia TTS) are
          // distinct columns (stt_configuration.language / tts_configuration.language).
          // mergedConfig spreads tts_configuration after stt_configuration, so
          // without these explicit overrides the TTS value would clobber the
          // STT field above and the STT value would never reach tts_language.
          language: stt_configuration?.language || mergedConfig?.language || "en",
          tts_language: tts_configuration?.language || "en",
          stt_provider: stt_provider || "deepgram",
          stt_model: stt_model || "nova-2",
          enable_language_detection: stt_configuration?.enable_language_detection || false,
          transcriber_ws_url: stt_configuration?.wsUrl || "",
          stt_sample_rate: stt_configuration?.sampleRate || undefined,
          stt_mode: stt_configuration?.mode || undefined,
        };

        // Use reset for the form structure
        methods.reset(resetData);

        // Force specific critical values with multiple setValue calls to ensure they stick
        // This is more reliable than relying on reset alone
        methods.setValue("llm_provider", resetData.llm_provider, { shouldValidate: false });
        methods.setValue("llm_model", resetData.llm_model, { shouldValidate: false });
        methods.setValue("assistantName", resetData.assistantName, { shouldValidate: false });

        // Force the values multiple times with increasing delays to overcome any race conditions
        // This resolves timing issues where other components clear form values during initialization
        const forceSetValues = (delay: number) => {
          setTimeout(() => {
            methods.setValue("llm_provider", resetData.llm_provider, { shouldValidate: false, shouldDirty: false, shouldTouch: false });
            methods.setValue("llm_model", resetData.llm_model, { shouldValidate: false, shouldDirty: false, shouldTouch: false });
          }, delay);
        };

        // Set values at multiple time intervals to ensure they persist
        // Extended timing to handle edge cases
        forceSetValues(100);
        forceSetValues(200);
        forceSetValues(500);
        forceSetValues(1000);
        forceSetValues(1500);
        forceSetValues(2000);

        // Final validation pass to clear any stale errors
        setTimeout(() => {
          methods.trigger(["llm_model", "llm_provider"]);
        }, 2500);
      }
    } catch (error) {
      console.error("Error fetching assistant:", error);
      toast({
        title: "Error",
        description: "Failed to fetch assistant details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (data: AssistantFormValues) => {
    console.log("Form submission started");
    console.log("Form data:", data);
    setAssistantName(data.assistantName);

    try {
      console.log("Form state:", {
        isDirty: methods.formState.isDirty,
        isSubmitting: methods.formState.isSubmitting,
        isValid: methods.formState.isValid,
        submitCount: methods.formState.submitCount,
      });

      const isValid = await methods.trigger(undefined, { shouldFocus: true });
      console.log("Form validation result:", isValid);

      if (!isValid) {
        const errorTabs: string[] = [];
        const errors = methods.formState.errors;

        // Log all form data and errors in detail
        logFormData();
        console.error(
          "Form validation errors:",
          JSON.stringify(errors, null, 2)
        );

        // Create a formatted error message with specific field errors
        const errorDetails: string[] = [];

        Object.entries(errors).forEach(([field, error]) => {
          if (error) {
            errorDetails.push(`${field}: ${error.message}`);
          }
        });

        if (
          errors.assistantName ||
          errors.llm_provider ||
          errors.llm_model ||
          errors.temperature ||
          errors.maxTokens ||
          errors.stt_provider ||
          errors.stt_model ||
          errors.language ||
          errors.voice ||
          errors.speed ||
          errors.pitch ||
          errors.backgroundSoundUrl
        ) {
          errorTabs.push("agent");
        }

        if (errors.functions) {
          errorTabs.push("functions");
        }

        if (
          errors.systemPrompt ||
          errors.contextWindow ||
          errors.responseFormat
        ) {
          errorTabs.push("prompt");
        }

        // Show toast with error information
        toast({
          title: "Validation Error",
          description: (
            <div className="space-y-2">
              <p>
                Please check the following tabs for errors:{" "}
                {errorTabs.join(", ")}
              </p>
              {errorDetails.length > 0 && (
                <div className="text-xs mt-2 max-h-40 overflow-y-auto">
                  <strong>Error details:</strong>
                  <ul className="list-disc pl-4 mt-1">
                    {errorDetails.map((err, index) => (
                      <li key={index}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ),
          variant: "destructive",
        });

        if (errorTabs.length > 0) {
          setActiveTab(errorTabs[0]);
        }

        return;
      }

      console.log("Form validation passed, proceeding with submission");
      console.log("Setting formData with llm_model:", data.llm_model);
      setFormData(data);
      setShowConfirmDialog(true);
    } catch (error) {
      console.error("Error during form validation:", error);
      toast({
        title: "Error",
        description: "An error occurred while validating the form",
        variant: "destructive",
      });
    }
  };

  const handleConfirmSubmit = async () => {
    if (!formData) return;

    setIsSaving(true);
    try {
      const { assistantName, llm_provider, llm_model, ...configurationData } =
        formData;

      console.log("handleConfirmSubmit - extracted llm_model:", llm_model);
      console.log("handleConfirmSubmit - full formData:", formData);

      const {
        temperature,
        maxTokens,
        detectEmotion,
        systemPrompt,
        summaryPrompt,
        evaluationPrompt,
        structuredJSONSchema,
        structuredDataPrompt,
        contextWindow,
        responseFormat,
        enableStreaming,

        stt_provider,
        stt_model: deepgramModel,
        language,
        enableFiltering,
        useNumerals,
        backgroundDenoising,
        keyterms,
        endpointing_ms,
        enable_language_detection,
        transcriber_ws_url,
        stt_sample_rate,
        stt_mode,

        voice,
        tts_model,
        tts_language,
        speed,
        pitch,
        stability,
        similarity_boost,
        style,
        clarity,
        styleExaggeration,
        backgroundSound,
        backgroundSoundUrl,
        inputMinCharacters,
        punctuationBoundaries,
        optimizeStreaming,
        useSpeakerBoost,

        firstMessage,
        endCallMessage,
        idleMessage,
        idleHangUpMessage,

        vadMinSpeechDuration,
        vadMinSilenceDuration,
        vadPrefixPaddingDuration,
        vadActivationThreshold,

        minEndpointingDelay,
        maxEndpointingDelay,

        knowledgeBaseEnabled,
        knowledgeBaseSearchThreshold,
        knowledgeBaseMaxResults,
        knowledgeBasePromptTemplate,

        turnDetectionEnabled,
        turnDetectionThreshold,
        minInterruptionDuration,

        background_audio_enabled,
        ambient_sound_type,
        ambient_sound_volume,
        thinking_sound_type,
        thinking_sound_volume,
        enableBVC,
        bvcMode,
        fillerEnabled,
        fillerPhrases,
        enableWaitForUser,
        enableCallScreening,
        enableEndCall,
        enableAnsweringMachine,
        enableCheckCtcBudget,
        enableSearchKnowledge,
        enableLoanEligibility,
        enableStartTechnicalQuestions,
        enableContactForm,

        // Custom/LiveKit-OpenAI provider config (handled separately in llmConfig)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        baseUrl: _baseUrl,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        apiKey: _apiKey,
        // Belongs to llm_configuration, not the generic configuration blob
        serviceTier,

        // Realtime provider config (handled separately in llmConfig)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        realtimeVoice: _realtimeVoice,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        realtimeLanguage: _realtimeLanguage,

        ...restConfig
      } = configurationData as typeof configurationData & {
        baseUrl?: string;
        apiKey?: string;
        realtimeVoice?: string;
        realtimeLanguage?: string;
      } & Record<string, unknown>;

      const llmConfig: Record<string, unknown> = {
        temperature,
        maxTokens,
        detectEmotion,
        contextWindow,
        responseFormat,
        enableStreaming,
      };

      // Persist base URL and API key for custom/livekit-openai/openai-realtime providers
      if (
        llm_provider === "custom" ||
        llm_provider === "livekit-openai" ||
        llm_provider === "openai-realtime" ||
        llm_provider === "custom-realtime"
      ) {
        const { baseUrl, apiKey } = configurationData as { baseUrl?: string; apiKey?: string };
        if (baseUrl) llmConfig.baseUrl = baseUrl;
        if (apiKey) llmConfig.apiKey = apiKey;
      }

      // Persist voice/language for realtime speech-to-speech providers (Gemini Live)
      if (llm_provider === "google-realtime") {
        const { realtimeVoice, realtimeLanguage } = configurationData as {
          realtimeVoice?: string;
          realtimeLanguage?: string;
        };
        llmConfig.realtimeVoice = realtimeVoice || "Puck";
        llmConfig.realtimeLanguage = realtimeLanguage || "en-US";
      }

      // service_tier is an OpenAI-only request parameter. Always written for
      // openai so the backend never has to guess a default.
      if (llm_provider === "openai") {
        llmConfig.serviceTier = serviceTier || "priority";
      }

      const sttConfig: Record<string, unknown> = {
        deepgramModel,
        language,
        enableFiltering,
        useNumerals,
        backgroundDenoising,
        keyterms: keyterms ? keyterms.split(' ').filter((term: string) => term.trim() !== '') : [],
        endpointing_ms,
        enable_language_detection,
      };

      if (stt_provider === "whisper_ws" && transcriber_ws_url) {
        sttConfig.wsUrl = transcriber_ws_url;
      }

      // Only Gnani and Smallest AI read a sample rate off the STT config.
      if (["gnani", "smallestai"].includes(stt_provider) && stt_sample_rate) {
        sttConfig.sampleRate = stt_sample_rate;
      }

      // Only Sarvam realtime reads mode off the STT config (see
      // AgentService.buildCallConfig's sarvam-realtime crossover, v2 server).
      if (stt_provider === "sarvam-realtime") {
        sttConfig.mode = stt_mode || "codemix";
      }

      const voiceId = voice ? String(voice).trim() : "21m00Tcm4TlvDq8ikWAM";

      // Convert Cartesia string speed values to numeric values for backend compatibility
      let numericSpeed = speed;
      if (formData.tts_provider === 'cartesia' && typeof speed === 'string') {
        switch (speed) {
          case 'slow':
            numericSpeed = 0.8;
            break;
          case 'fast':
            numericSpeed = 1.2;
            break;
          case 'normal':
          default:
            numericSpeed = 1.0;
            break;
        }
      }

      const ttsConfig: Record<string, unknown> = {
        voice: voiceId,
        tts_model,
        speed: numericSpeed,
        pitch,
        stability,
        similarity_boost,
        style,
        clarity,
        styleExaggeration,
        backgroundSound,
        backgroundSoundUrl,
        inputMinCharacters,
        punctuationBoundaries,
        optimizeStreaming,
        useSpeakerBoost,
      };

      // Cartesia-only: Murf's own Language field still writes the shared STT
      // `language` key (untouched here), so setting this unconditionally would
      // leak a stale Cartesia selection into tts_configuration for Murf agents.
      if (formData.tts_provider === "cartesia" && tts_language) {
        ttsConfig.language = tts_language;
      }

      console.log("TTS config with voice ID:", ttsConfig.voice);

      console.log("Organization ID will be handled by auth.oid() in RLS");

      const extractVariables = (text: string) => {
        if (!text) return [];
        const regex = /\{([^}]+)\}/g;
        const matches = [];
        let match;

        while ((match = regex.exec(text)) !== null) {
          if (!matches.includes(match[1])) {
            matches.push(match[1]);
          }
        }

        return matches;
      };

      const allPromptText = [
        firstMessage,
        systemPrompt,
        endCallMessage,
        summaryPrompt,
        evaluationPrompt,
        structuredDataPrompt,
        restConfig?.voicemailMessage,
        restConfig?.idleMessages,
        idleMessage,
        idleHangUpMessage,
      ]
        .filter(Boolean)
        .join(" ");

      const extractedVariables = extractVariables(allPromptText);

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error("User authentication required");
      }

      const now = new Date().toISOString();

      // Add VAD configuration to restConfig
      const vadConfig = {
        min_speech_duration: vadMinSpeechDuration,
        min_silence_duration: vadMinSilenceDuration,
        prefix_padding_duration: vadPrefixPaddingDuration,
        activation_threshold: vadActivationThreshold,
      };

      // Add endpointing delay configuration
      const endpointingConfig = {
        min_endpointing_delay: minEndpointingDelay,
        max_endpointing_delay: maxEndpointingDelay,
      };

      // Add knowledge base configuration
      const knowledgeBaseConfig = {
        enabled: knowledgeBaseEnabled,
        search_threshold: knowledgeBaseSearchThreshold,
        max_results: knowledgeBaseMaxResults,
        prompt_template: knowledgeBasePromptTemplate,
      };

      const configurationWithEnhancements = {
        ...restConfig,
        vad: vadConfig,
        endpointing: endpointingConfig,
        knowledge_base: knowledgeBaseConfig,
        turnDetectionEnabled,
        turnDetectionThreshold,
        minInterruptionDuration,
        enableBVC,
        bvcMode,
        fillerEnabled,
        fillerPhrases,
        enableWaitForUser,
        enableCallScreening,
        enableEndCall,
        enableAnsweringMachine,
        enableCheckCtcBudget,
        enableSearchKnowledge,
        enableLoanEligibility,
        enableStartTechnicalQuestions,
        enableContactForm,
      };

      const assistantData = {
        ...(assistantId && { id: assistantId }),
        name: assistantName,
        llm_model: llm_model,
        status: "published",
        first_message: firstMessage,
        end_call_message: endCallMessage,
        system_prompt: systemPrompt,
        summary_prompt: summaryPrompt,
        evaluation_prompt: evaluationPrompt,
        structured_json_schema: structuredJSONSchema,
        structured_data_prompt: structuredDataPrompt,
        idle_message: idleMessage,
        idle_hangup_message: idleHangUpMessage,
        configuration: configurationWithEnhancements,
        llm_configuration: llmConfig,
        stt_configuration: sttConfig,
        tts_configuration: ttsConfig,
        llm_provider: llm_provider,
        stt_provider: formData.stt_provider,
        stt_model: formData.stt_model,
        tts_provider: formData.tts_provider,
        tts_model: formData.tts_model,
        // Empty string means "no per-agent preference" — store null, not "",
        // so the V2 server's fallback chain (request > agent > server default)
        // reads it as absent rather than as an unrecognised provider name.
        telephony_provider: formData.telephony_provider || null,
        use_integrated_agent: formData.useIntegratedAgent,
        variables: extractedVariables.length > 0 ? extractedVariables : [],
        background_audio_enabled,
        ambient_sound_type,
        ambient_sound_volume,
        thinking_sound_type,
        thinking_sound_volume,
        updated_by: userData.user.id,
        updated_at: now,
      };

      console.log("assistantData before save:", assistantData);
      console.log("assistantData.llm_model before save:", assistantData.llm_model);

      let result;

      if (assistantId) {
        const { data, error } = await supabase
          .from("assistants")
          .update(assistantData)
          .eq("id", assistantId)
          .select();

        if (error) throw error;
        result = data ? data[0] : null;

        console.log("Database save result:", result);
        console.log("Database result llm_model:", result?.llm_model);

        if (!result) {
          throw new Error(`Assistant with ID ${assistantId} not found`);
        }
      } else {
        const { data, error } = await supabase
          .from("assistants")
          .insert({
            ...assistantData,
            created_by: userData.user.id,
            created_at: now,
          })
          .select()
          .single();

        if (error) throw error;
        result = data;

        console.log("Database insert result:", result);
        console.log("Database insert result llm_model:", result?.llm_model);
      }

      const versionNotes = "Assistant created/updated via dashboard";

      if (result?.id) {
        const { data: versions } = await supabase
          .from("assistant_versions")
          .select("version_number")
          .eq("assistant_id", result.id)
          .order("version_number", { ascending: false })
          .limit(1);

        const nextVersionNumber =
          versions && versions.length > 0 ? versions[0].version_number + 1 : 1;

        await supabase.from("assistant_versions").insert({
          assistant_id: result.id,
          version_number: nextVersionNumber,
          configuration: configurationWithEnhancements,
          llm_configuration: llmConfig,
          stt_configuration: sttConfig,
          tts_configuration: ttsConfig,
          notes: versionNotes,
          created_by: userData.user.id,
        });
      }

      await supabase.from("audit_logs").insert({
        user_id: userData.user.id,
        action: assistantId ? "update" : "create",
        resource_type: "assistant",
        resource_id: result?.id,
        details: {
          name: assistantName,
          requested_org_id: null,
          actual_org_id: null,
        },
      });

      toast({
        title: "Success! 🎉",
        description: (
          <div className="space-y-2">
            <p>
              Assistant "{assistantName}" has been successfully{" "}
              {assistantId ? "updated" : "created"}.
            </p>
            <p className="text-xs text-muted-foreground">
              You can now find it in your assistants list
            </p>
          </div>
        ),
        variant: "default",
      });

      if (assistantId && assistantName) {
        onAssistantUpdated?.(assistantId, { name: assistantName });
      }

    } catch (error: any) {
      console.error("Error saving assistant:", error);

      // Log the full error details
      console.error("Save error details:", {
        message: error.message,
        stack: error.stack,
        ...(error.response && {
          response: {
            status: error.response.status,
            data: error.response.data,
          },
        }),
      });

      if (error.message?.includes("organization")) {
        toast({
          title: "Organization Required",
          description:
            "You need to create or join an organization before creating assistants.",
          variant: "destructive",
        });
      } else if (
        error.code === "PGRST301" ||
        error?.message?.includes("foreign key constraint")
      ) {
        // Handle foreign key constraint errors
        toast({
          title: "Reference Error",
          description: (
            <div className="space-y-2">
              <p>There was an error with referenced data.</p>
              <div className="text-xs mt-2">
                <p>
                  <strong>Details:</strong> {error.message}
                </p>
                <p className="text-muted-foreground mt-1">
                  This may be due to missing referenced data or a permission
                  issue.
                </p>
              </div>
            </div>
          ),
          variant: "destructive",
        });
      } else if (
        error.code === "23505" ||
        error?.message?.includes("duplicate key")
      ) {
        // Handle duplicate key errors
        toast({
          title: "Duplicate Error",
          description: (
            <div className="space-y-2">
              <p>An item with this name already exists.</p>
              <div className="text-xs mt-2">
                <p className="text-muted-foreground">
                  Please use a different name or update the existing item.
                </p>
              </div>
            </div>
          ),
          variant: "destructive",
        });
      } else {
        // Generic error handling with more details
        toast({
          title: "Save Failed",
          description: (
            <div className="space-y-2">
              <p>
                Failed to save assistant. Please check your connection and try
                again.
              </p>
              <div className="text-xs mt-2 max-h-40 overflow-y-auto">
                <strong>Error details:</strong>
                <p className="text-danger mt-1">
                  {error.message || "Unknown error"}
                </p>
              </div>
            </div>
          ),
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
      setShowConfirmDialog(false);
    }
  };

  const handleDeleteAssistant = async () => {
    if (!assistantId) return;

    try {
      const { error } = await supabase
        .from("assistants")
        .delete()
        .eq("id", assistantId);

      if (error) throw error;

      toast({
        title: "Success! 🗑️",
        description: `Assistant "${assistantName}" has been deleted successfully.`,
      });

      setShowDeleteDialog(false);
      onAssistantDeleted?.(assistantId);
    } catch (error: any) {
      console.error("Error deleting assistant:", error);
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete the assistant.",
        variant: "destructive",
      });
    }
  };

  const handlePublishToPlatform = async ({
    type,
    featureType,
    version,
  }: {
    type: AgentPublishType;
    featureType: AgentPublishFeatureType;
    version: AgentPublishVersion;
  }) => {
    if (!assistantId) {
      toast({
        title: "Save Required",
        description: "Please save the assistant before publishing it.",
        variant: "destructive",
      });
      return;
    }

    setIsPublishingPlatform(true);
    try {
      const name = methods.getValues("assistantName") || assistantName;

      await publishAgent({
        name,
        sourceId: assistantId,
        source: "voxi",
        type,
        featureType,
        version,
      });

      const { error } = await supabase
        .from("assistants")
        .update({ status: "published", updated_at: new Date().toISOString() })
        .eq("id", assistantId);

      if (error) {
        throw error;
      }

      onAssistantUpdated?.(assistantId, { name, status: "published" });

      toast({
        title: "Assistant Published",
        description: `"${name}" has been published successfully.`,
      });
      setShowPublishDialog(false);
    } catch (error: any) {
      console.error("Error publishing assistant:", error);
      toast({
        title: "Publish Failed",
        description: error.message || "Failed to publish assistant.",
        variant: "destructive",
      });
    } finally {
      setIsPublishingPlatform(false);
    }
  };

  const resetForm = () => {
    if (
      confirm(
        "Are you sure you want to reset all form fields to default values?"
      )
    ) {
      methods.reset();
      toast({
        title: "Form Reset",
        description: "All form fields have been reset to default values.",
      });
    }
  };

  const togglePreview = () => {
    if (!showPreview) {
      const isValid = methods.trigger();
      if (isValid) {
        setFormData(methods.getValues());
        setShowPreview(true);
      } else {
        const errors = methods.formState.errors;
        const errorTabs: string[] = [];

        if (
          errors.assistantName ||
          errors.temperature ||
          errors.maxTokens ||
          errors.llm_provider ||
          errors.llm_model ||
          errors.stt_provider ||
          errors.stt_model ||
          errors.tts_provider ||
          errors.tts_model ||
          errors.language ||
          errors.voice ||
          errors.speed ||
          errors.pitch ||
          errors.backgroundSoundUrl
        ) {
          errorTabs.push("agent");
        }

        if (errors.functions) {
          errorTabs.push("functions");
        }

        if (
          errors.systemPrompt ||
          errors.contextWindow ||
          errors.responseFormat
        ) {
          errorTabs.push("prompt");
        }

        setTabsWithErrors(errorTabs);

        if (errorTabs.length > 0) {
          setActiveTab(errorTabs[0]);

          toast({
            title: "Validation Error",
            description: `Please fix the errors in the ${errorTabs.join(
              ", "
            )} tab(s) before previewing.`,
            variant: "destructive",
          });
        }
      }
    } else {
      setShowPreview(false);
    }
  };

  const handleTabChange = (value: string) => {
    const currentFormState = methods.formState;

    if (currentFormState.isDirty) {
      methods.trigger().then((isValid) => {
        if (!isValid) {
          const errors = methods.formState.errors;
          const errorTabs: string[] = [];

          if (
            errors.assistantName ||
            errors.temperature ||
            errors.maxTokens ||
            errors.llm_provider ||
            errors.llm_model ||
            errors.stt_provider ||
            errors.stt_model ||
            errors.tts_provider ||
            errors.tts_model ||
            errors.language ||
            errors.voice ||
            errors.speed ||
            errors.pitch ||
            errors.backgroundSoundUrl
          ) {
            errorTabs.push("agent");
          }

          if (errors.functions) {
            errorTabs.push("functions");
          }

          if (
            errors.systemPrompt ||
            errors.contextWindow ||
            errors.responseFormat
          ) {
            errorTabs.push("prompt");
          }

          setTabsWithErrors(errorTabs);
        } else {
          setTabsWithErrors([]);
        }
      });
    }

    setActiveTab(value);
  };

  const renderAssistantPreview = () => {
    if (!formData) return null;

    return (
      <Card className="mt-6">
        <CardContent className="p-6">
          <h2 className="text-2xl font-bold mb-4 text-foreground">
            Assistant Preview
          </h2>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
              <ul className="space-y-2">
                <li>
                  <span className="font-medium">Name:</span>{" "}
                  {formData.assistantName}
                </li>
                <li>
                  <span className="font-medium">Model:</span>{" "}
                  {formData.llm_provider} / {formData.llm_model}
                </li>
                <li>
                  <span className="font-medium">Temperature:</span>{" "}
                  {formData.temperature}
                </li>
                <li>
                  <span className="font-medium">Max Tokens:</span>{" "}
                  {formData.maxTokens}
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">
                Voice & Transcription
              </h3>
              <ul className="space-y-2">
                <li>
                  <span className="font-medium">Voice Provider:</span>{" "}
                  {formData.tts_provider}
                </li>
                <li>
                  <span className="font-medium">Transcriber:</span>{" "}
                  {formData.stt_provider}
                </li>
                <li>
                  <span className="font-medium">Language:</span>{" "}
                  {formData.language}
                </li>
                <li>
                  <span className="font-medium">Speed:</span> {formData.speed}x
                </li>
                <li>
                  <span className="font-medium">Emotion Detection:</span>{" "}
                  {formData.detectEmotion ? "Enabled" : "Disabled"}
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">System Prompt</h3>
            <div className="bg-muted p-3 rounded-md">
              <p className="whitespace-pre-wrap">
                {formData.systemPrompt || "No system prompt defined."}
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowPreview(false)}
              className="mr-2"
            >
              Close Preview
            </Button>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              className="bg-good text-background hover:bg-good font-bold"
            >
              Publish Assistant
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Debugging utility function to log form state
  const logFormData = () => {
    console.group("Assistant Form Data");
    console.log("Form values:", methods.getValues());
    console.log("Form errors:", methods.formState.errors);
    console.log("Form state:", {
      isDirty: methods.formState.isDirty,
      isSubmitting: methods.formState.isSubmitting,
      isValid: methods.formState.isValid,
      submitCount: methods.formState.submitCount,
    });
    console.groupEnd();
    return true;
  };

  return (
    <div>
      {isLoading ? (
        <div className="flex items-center justify-center h-[calc(100vh-12rem)]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div>
      <div className="mb-8 border-b pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">
              {assistantId
                ? `Edit Assistant: ${assistantName}`
                : "Create New Assistant"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {assistantId
                ? "Update your assistant's configuration"
                : "Configure your new AI assistant"}
              {!isPro && " (Standard Account)"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {assistantId && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPublishDialog(true)}
                disabled={isPublishingPlatform}
              >
                {isPublishingPlatform ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UploadCloud className="mr-2 h-4 w-4" />
                )}
                Publish
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onCancelAssistantSelection?.(location.search)}
              className="mr-2"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleFormSubmit)}
          className="space-y-8"
        >
          <Tabs
            defaultValue="agent"
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            <TabsList className={`grid ${assistantType === 'multi' ? 'grid-cols-5' : 'grid-cols-4'} mb-8 justify-between`}>
                <TabsTrigger
                  value="agent"
                  className={
                    tabsWithErrors.includes("tts_provider") ||
                    tabsWithErrors.includes("stt_provider") ||
                    tabsWithErrors.includes("tts_provider")
                      ? "border-2 border-destructive"
                      : ""
                  }
                >
                  Agent
                </TabsTrigger>
                <TabsTrigger
                  value="prompt"
                  className={
                    tabsWithErrors.includes("prompt")
                      ? "border-2 border-destructive"
                      : ""
                  }
                >
                  Prompt
                </TabsTrigger>
                <TabsTrigger
                  value="configuration"
                  className={
                    tabsWithErrors.includes("prompt")
                      ? "border-2 border-destructive"
                      : ""
                  }
                >
                  Configuration
                </TabsTrigger>
                <TabsTrigger
                  value="knowledge_base"
                  className={
                    tabsWithErrors.includes("knowledge_base")
                      ? "border-2 border-destructive"
                      : ""
                  }
                >
                  Knowledge Base
                </TabsTrigger>
                {/* <TabsTrigger
                  value="call_logs"
                  className={
                    tabsWithErrors.includes("calls")
                      ? "border-2 border-destructive"
                      : ""
                  }
                >
                  Call Logs
                </TabsTrigger> */}
                {assistantType === 'multi' && (
                  <TabsTrigger
                    value="sub_agents"
                    className={
                      tabsWithErrors.includes("sub_agents")
                        ? "border-2 border-destructive"
                        : ""
                    }
                  >
                    Sub-Agents
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="agent">
                <AgentTab key={assistantId || 'new'} />
              </TabsContent>

              <TabsContent value="prompt">
                <PromptTab />
              </TabsContent>
              <TabsContent value="configuration">
                <ConfigurationTab />
              </TabsContent>

              <TabsContent value="knowledge_base">
                <KnowledgeBaseTab assistantId={assistantId} />
              </TabsContent>

              {/* <TabsContent value="call_logs">
                <LeadCallLogsTab assistantId={assistantId} />
              </TabsContent> */}
              {assistantType === 'multi' && (
                <TabsContent value="sub_agents">
                  <SubAgentsTab assistantId={assistantId} />
                </TabsContent>
              )}
          </Tabs>

          {showPreview && renderAssistantPreview()}

          <div className="mt-8 flex justify-between">
            <div className="flex space-x-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Reset Form
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset all form fields to default values</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={togglePreview}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      {showPreview ? "Hide Preview" : "Preview Assistant"}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Preview your assistant configuration</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <SampleCodeDialog assistantConfig={methods.getValues()} /> */}
              <TestAgentDialog assistantConfig={methods.getValues()} assistantId={assistantId} />
              {/* <SimpleTestAgent /> */}
            </div>

            <div className="flex gap-2">
              {assistantId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Assistant
                </Button>
              )}
              <Button
                type="submit"
                className="bg-good text-background hover:bg-good font-bold"
                isLoading={isSaving || methods.formState.isSubmitting}
                loadingText="Saving..."
              >
                <Save className="mr-2 h-4 w-4" />
                {assistantId ? "Update Assistant" : "Publish Assistant"}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
      </div>)}

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Assistant Publication</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {assistantId ? "update" : "publish"} this
              assistant? This will make it available for use in your
              organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSubmit}
              disabled={isSaving}
              className="bg-good text-background hover:bg-good"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PublishAgentDialog
        assistantName={methods.getValues("assistantName") || assistantName}
        open={showPublishDialog}
        isPublishing={isPublishingPlatform}
        onOpenChange={(open) => {
          if (!isPublishingPlatform) {
            setShowPublishDialog(open);
          }
        }}
        onPublish={handlePublishToPlatform}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Assistant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{assistantName}"? This action cannot be undone and will permanently remove the assistant and all its configurations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAssistant}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Assistant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
